import {useAuth} from '../../auth/use-auth';
import {memo, useEffect, useId, useMemo, useRef} from 'react';
import lazyLoader from '../../utils/http/lazy-loader';
import clsx from 'clsx';
import {useSettings} from '../../core/settings/use-settings';
import dot from 'dot-object';
import {Settings} from '@common/core/settings/settings';

interface AdHostProps {
  slot: keyof Omit<NonNullable<Settings['ads']>, 'disable'>;
  className?: string;
  style?: React.CSSProperties;
}

export function AdHost({slot, className, style}: AdHostProps) {
  const settings = useSettings();
  const {isSubscribed} = useAuth();
  const adCode = useMemo(() => dot.pick(`ads.${slot}`, settings), [slot, settings]);

  if (settings.ads?.disable || isSubscribed || !adCode) return null;
  return <InvariantAd className={className} slot={String(slot)} adCode={adCode} style={style} />;
}

interface InvariantAdProps {
  slot: string;
  adCode: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Strategy:
 *  - Start with: hidden + pending placeholder (reserves small space).
 *  - If MEANINGFUL content appears quickly => show and remove placeholder (no jump).
 *  - If no-fill, remove placeholder early (1.2s) so page snaps up before user notices.
 *  - Hard failsafe remains at 8s as a backup.
 */
const InvariantAd = memo(
  ({slot, adCode, className, style}: InvariantAdProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const id = useId();

    useEffect(() => {
      const host = ref.current;
      if (!host) return;

      // Start state: hidden + pending (reserve minimal space to reduce CLS)
      host.classList.add('ad-hidden', 'ad-pending');

      // 1) Inject static HTML (without script tags)
      host.innerHTML = getAdHtml(adCode);

      // 2) Load external scripts then execute inline JS
      loadAdScripts(adCode, host).then(() => executeAdJavascript(adCode, id));

      // 3) Decide show/hide by looking ONLY at children heights (not host)
      let decided = false;
      const QUICK_DECIDE_MS = 1200;       // اگر چیزی نیامد، خیلی زود placeholder را جمع کن
      const FAILSAFE_MS = 8000;           // پشتیبان
      const MIN_EFFECTIVE_HEIGHT = 30;

      const hasMeaningfulContent = () => {
        const ifr = host.querySelector('iframe') as HTMLIFrameElement | null;
        if (ifr) {
          const r = ifr.getBoundingClientRect();
          if (r.height >= MIN_EFFECTIVE_HEIGHT && r.width >= 30) return true;
        }
        const kids = Array.from(host.children) as HTMLElement[];
        for (const el of kids) {
          const r = el.getBoundingClientRect();
          if (r.height >= MIN_EFFECTIVE_HEIGHT && r.width >= 30) return true;
        }
        return false;
      };

      const showHost = () => {
        if (!decided) {
          decided = true;
          host.classList.remove('ad-hidden', 'ad-pending');
        }
      };
      const keepHidden = () => {
        if (!decided) {
          decided = true;
          host.classList.add('ad-hidden');
          host.classList.remove('ad-pending');
        }
      };
      const removePendingOnly = () => {
        // هنوز تصمیم نهایی نه؛ فقط placeholder را بردار تا اگر no-fill است، بالا کشیده شود
        host.classList.remove('ad-pending');
      };

      const obs = new MutationObserver(() => {
        requestAnimationFrame(() => {
          if (hasMeaningfulContent()) {
            showHost();
            clearTimeout(quick);
            clearTimeout(failsafe);
            obs.disconnect();
          }
        });
      });
      obs.observe(host, {childList: true, subtree: true, attributes: true});

      // 3a) Quick window: اگر تا QUICK_DECIDE_MS چیزی نیامد، placeholder را بردار
      const quick = setTimeout(() => {
        if (!hasMeaningfulContent()) removePendingOnly();
      }, QUICK_DECIDE_MS);

      // 3b) Hard failsafe: اگر تا ۸ ثانیه هم چیزی نبود، مخفی بماند
      const failsafe = setTimeout(() => {
        if (!hasMeaningfulContent()) keepHidden();
      }, FAILSAFE_MS);

      // اگر از همان اول محتوای واقعی بود (برخی شبکه‌ها بسیار سریع‌اند)
      if (hasMeaningfulContent()) {
        showHost();
        clearTimeout(quick);
        clearTimeout(failsafe);
        obs.disconnect();
      }

      return () => {
        clearTimeout(quick);
        clearTimeout(failsafe);
        obs.disconnect();
      };
    }, [adCode, id]);

    return (
      <div
        ref={ref}
        id={id}
        className={clsx(
          // ⚠️ هیچ min-height ثابتی اینجا نگذارید
          'ad-host flex w-full max-w-full items-center justify-center overflow-hidden',
          `${slot.replace(/\./g, '-')}-host`,
          className,
        )}
        dangerouslySetInnerHTML={{__html: ''}}
        style={style}
      />
    );
  },
  () => true,
);

// ---------- helpers ----------
function getAdHtml(adCode: string) {
  return adCode?.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '').trim();
}
function loadAdScripts(adCode: string, parentEl: HTMLDivElement): Promise<any> {
  const promises: Promise<any>[] = [];
  const pattern = /<script.*?src=['"](.*?)['"]/g;
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(adCode))) {
    if (m[1]) promises.push(lazyLoader.loadAsset(m[1], {type: 'js', parentEl}));
  }
  return Promise.all(promises);
}
function executeAdJavascript(adCode: string, id: string) {
  const pattern = /<script\b[^>]*>([\s\S]*?)<\/script>/g;
  let content: RegExpExecArray | null;
  while ((content = pattern.exec(adCode))) {
    if (!content[1]) continue;
    const container = document.getElementById(id);
    const match = content[1].match(/document.write\((.+?)\);/);
    if (match) {
      const d = document.createElement('div');
      d.innerHTML = match[1];
      if (d.firstChild && container) container.appendChild(d.firstChild);
    }
    const cleaned = content[1].replace(/document.write\((.+?)\);/, '');
    const s = document.createElement('script');
    s.type = 'text/javascript';
    s.text = cleaned;
    (document.head || document.body).appendChild(s);
  }
}
